import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory, Link } from 'react-router-dom';

import PbxContext from '../../../context/PbxContext';
import { accessLocalStorage, valuesAnalyze, changeViewForms, registerUser, exportSelectAllQueues, exportSelectAllEndpoints, readAllTenant } from '../../../services';
import { scopeByTenant } from '../../../services/tenantScope';
import Loading from '../../../components/Loading/LoadingModule';
import PermissionsTree from './PermissionsTree';
import FilterableSelect from '../../Common/FilterableSelect';

import '../../../libs/bulma.min.css';
import './UsersForm.css';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSave, faArrowLeft, faUser, faShieldAlt, faList } from '@fortawesome/free-solid-svg-icons';
import validEmail from '../../../services/validEmail';

function UsersCreate() {
  const getItensStateGlobal = useContext(PbxContext);
  const { toggleIsHidden, toggleIsChangeFormElements } = getItensStateGlobal;

  const history = useHistory();

  const [loading, setLoading] = useState(true);
  const [isRootVgs, setIsRootVgs] = useState(false);
  const [values, setValues] = useState({
    name: '',
    email: '',
    password: '',
    endpoint: '',
    role: '',
    tenant: '',
    tenants: [],
    endpointsviewer: [],
    queuesviewer: [],
    enabled_modules: null,
    active: true
  });
  const [validate, setValidate] = useState({});
  const [disable, setDisable] = useState(true);
  const [saving, setSaving] = useState(false);
  const [endpoints, setEndpoints] = useState([]);
  const [queues, setQueues] = useState([]);
  const [tenants, setTenants] = useState([]);

  const [alert, setAlert] = useState({
    show: false,
    type: '',
    message: ''
  });

  const { changeBorderImputToRedOrNormal, changeVisibityMessages } = changeViewForms;

  const validateUserLogged = useCallback(async () => {
    const dataUser = accessLocalStorage.getUserLocalStorage();
    if (!dataUser) return history.push('/');
    
    const { user: { role, idtenant } } = dataUser;
    if (role !== 'root') return history.push('/home');

    const isVgs = role === 'root' && idtenant === 'vgs';
    setIsRootVgs(isVgs);

    try {
      let fetchQueues = await exportSelectAllQueues();
      fetchQueues = scopeByTenant(fetchQueues, (queue) => queue.accountcodequeue, idtenant);
      const itensQueues = fetchQueues.map((queue) => queue.namequeue || queue.queuesselect).filter(q => q);

      let fetchEndpoints = await exportSelectAllEndpoints();
      fetchEndpoints = scopeByTenant(fetchEndpoints, (endpoint) => endpoint.tenant, idtenant);
      const itensEndpoints = fetchEndpoints.map((endpoint) => endpoint.endpoint).filter((e) => !isNaN(e));

      let fetchTenants = await readAllTenant();
      fetchTenants = scopeByTenant(fetchTenants, (tenant) => tenant.short_name, idtenant);

      setEndpoints(itensEndpoints);
      setQueues(itensQueues);
      setTenants(fetchTenants);
      setLoading(false);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      setLoading(false);
    }
  }, [history]);

  useEffect(() => {
    validateUserLogged();
  }, [validateUserLogged]);

  const endpointOptions = useMemo(
    () => endpoints.map((endpoint) => ({ value: endpoint, label: endpoint })),
    [endpoints],
  );
  const endpointsViewerOptions = useMemo(
    () => [{ value: 'all', label: 'Todos' }, { value: 'none', label: 'Nenhum' }, ...endpointOptions],
    [endpointOptions],
  );
  const queueOptions = useMemo(
    () => queues.map((queue) => ({ value: queue, label: queue })),
    [queues],
  );
  const queuesViewerOptions = useMemo(
    () => [{ value: 'all', label: 'Todas' }, { value: 'none', label: 'Nenhuma' }, ...queueOptions],
    [queueOptions],
  );
  const tenantOptions = useMemo(
    () => tenants.map((tenant) => ({ value: tenant.short_name, label: `${tenant.name} (${tenant.short_name})` })),
    [tenants],
  );

  const changeScreen = (check, name) => {
    changeBorderImputToRedOrNormal(check, name);
    changeVisibityMessages(check, `${name}-invalid-data`);
  };

  const handleChangeDataAnalyze = (name, value) => {
    const { checkNumber, checkPassword, isTextAndSpace, checkText } = valuesAnalyze;

    if (name === 'endpointsviewer') {
      if (value === 'all' || value === 'none') return true;
      if (Array.isArray(value)) {
        const allValid = value.every(v => v === 'all' || v === 'none' || checkNumber(v));
        changeScreen(allValid, name);
        return allValid;
      }
      let checkValueIsNumber = checkNumber(value);
      changeScreen(checkValueIsNumber, name);
      return checkValueIsNumber;
    }

    if (name === 'queuesviewer') {
      if (value === 'all' || value === 'none') return true;
      if (Array.isArray(value)) {
        return true;
      }
      const checkValueIsText = checkText(value);
      changeScreen(checkValueIsText, name);
      return checkValueIsText;
    }

    if (name === 'email') {
      const emailIsValid = validEmail(value);
      changeScreen(emailIsValid, name);
      return emailIsValid;
    }

    if (name === 'password') {
      const passwordIsValid = checkPassword(value);
      changeScreen(passwordIsValid, name);
      return passwordIsValid;
    }

    if (name === 'role') {
      const checkValueIsText = checkText(value);
      changeScreen(checkValueIsText, name);
      return checkValueIsText;
    }

    if (name === 'name') {
      if (!value || value.trim() === '') {
        changeScreen(false, name);
        return false;
      }
      const trimmedValue = value.trim();
      if (trimmedValue.length < 2) {
        changeScreen(false, name);
        return false;
      }
      const checkValueIsCharacters = /^[a-zA-ZÀ-ÿ\s\.\-']+$/.test(trimmedValue);
      changeScreen(checkValueIsCharacters, name);
      return checkValueIsCharacters;
    }

    if (name === 'tenant') {
      const tenantsId = tenants.map((tenant) => tenant.short_name);
      const checkRealTenant = tenantsId.includes(value);
      changeScreen(checkRealTenant, name);
      return checkRealTenant;
    }

    if (name === 'tenants') {
      if (Array.isArray(value)) {
        const tenantsId = tenants.map((tenant) => tenant.short_name);
        const allValid = value.every(t => tenantsId.includes(t));
        changeScreen(allValid, name);
        return allValid;
      }
      return false;
    }

    return true;
  };

  const handleChangeValues = (item) => {
    const { name, value } = item;

    let processedValue = value;
    if (name === 'endpointsviewer' || name === 'queuesviewer' || name === 'tenants') {
      processedValue = Array.from(item.selectedOptions, (option) => option.value);
    }

    const itemStatus = handleChangeDataAnalyze(name, processedValue);
    setValidate((prevState) => ({
      ...prevState,
      [name]: itemStatus === undefined ? true : itemStatus
    }));

    setValues((prevState) => ({
      ...prevState,
      [name]: processedValue,
      ...(name === 'role' && processedValue !== 'tec_tenant' ? { enabled_modules: null } : {})
    }));

    const requiredFields = isRootVgs 
      ? ['name', 'email', 'password', 'role', 'tenants']
      : ['name', 'email', 'password', 'role', 'tenant'];
    const currentValidate = { ...validate, [name]: itemStatus === undefined ? true : itemStatus };
    const allRequiredFilled = requiredFields.every(field => {
      if (field === 'endpointsviewer' || field === 'queuesviewer' || field === 'tenants') {
        return Array.isArray(values[field]) ? values[field].length > 0 : false;
      }
      return values[field] !== '' && values[field] !== undefined;
    });
    const allValid = Object.values(currentValidate).every(v => v === true);
    
    setDisable(!(allValid && allRequiredFilled));
  };

  const endpointsViewerAnalyse = () => {
    if (values['endpointsviewer']) {
      if (values['endpointsviewer'].includes('all')) {
        values['endpointsviewer'] = ['all'];
      }
      if (values['endpointsviewer'].includes('none')) {
        values['endpointsviewer'] = ['none'];
      }
      if (values['endpointsviewer'].includes('all') && values['endpointsviewer'].includes('none')) {
        values['endpointsviewer'] = ['none'];
      }
    }
  };

  const queuesViewerAnalyse = () => {
    if (values['queuesviewer']) {
      if (values['queuesviewer'].includes('all')) {
        values['queuesviewer'] = ['all'];
      }
      if (values['queuesviewer'].includes('none')) {
        values['queuesviewer'] = ['none'];
      }
      if (values['queuesviewer'].includes('all') && values['queuesviewer'].includes('none')) {
        values['queuesviewer'] = ['none'];
      }
    }
  };

  const sendDataUsersGenerate = async () => {
    endpointsViewerAnalyse();
    queuesViewerAnalyse();

    setSaving(true);
    setAlert({ show: false, type: '', message: '' });

    try {
      if (isRootVgs && values.tenants && values.tenants.length > 0) {
        const tenantsToCreate = values.tenants;
        let successCount = 0;
        let errorCount = 0;
        let firstErrorMessage = '';
        const errors = [];

        for (const tenant of tenantsToCreate) {
          try {
            const userData = {
              ...values,
              tenant: tenant
            };
            delete userData.tenants;

            const response = await registerUser(userData);
            if (response && response.success) {
              successCount++;
            } else {
              errorCount++;
              errors.push(tenant);
              if (!firstErrorMessage) firstErrorMessage = (response && response.error) || '';
            }
          } catch (error) {
            errorCount++;
            errors.push(tenant);
            if (!firstErrorMessage) firstErrorMessage = 'Erro inesperado ao criar usuário.';
            console.error(`Erro ao criar usuário para tenant ${tenant}:`, error);
          }
        }

        if (successCount > 0) {
          setAlert({
            show: true,
            type: successCount === tenantsToCreate.length ? 'success' : 'warning',
            message: `${successCount} usuário(s) criado(s) com sucesso${errorCount > 0 ? `. ${errorCount} falha(s) para tenant(s): ${errors.join(', ')}` : ''}!`
          });
          
          setTimeout(() => {
            history.push('/config/usuarios/lista');
          }, 3000);
        } else {
          setAlert({
            show: true,
            type: 'danger',
            message: `Não foi possível criar nenhum usuário para o(s) cliente(s): ${errors.join(', ')}.${firstErrorMessage ? ` ${firstErrorMessage}` : ''}`
          });
        }
      } else {
        const userData = { ...values };
        delete userData.tenants;

        const response = await registerUser(userData);

        if (response && response.success) {
          setAlert({
            show: true,
            type: 'success',
            message: 'Usuário criado com sucesso!'
          });

          setTimeout(() => {
            history.push('/config/usuarios/lista');
          }, 2000);
        } else {
          setAlert({
            show: true,
            type: 'danger',
            message: (response && response.error) || 'Não foi possível criar o usuário. Verifique os dados e tente novamente.'
          });
        }
      }
    } catch (error) {
      console.error('Erro ao criar usuário:', error);
      setAlert({
        show: true,
        type: 'danger',
        message: 'Erro ao criar usuário. Tente novamente.'
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loading />;

  return (
    <div className="users-form-container">
      <div className="users-form-header">
        <div className="users-form-title-wrapper">
          <svg className="users-form-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
          </svg>
          <div>
            <h1 className="users-form-title">Novo Usuário</h1>
            <p className="users-form-subtitle">Criar um novo usuário no sistema</p>
          </div>
        </div>
        <Link to="/config/usuarios/lista" className="btn-back">
          <FontAwesomeIcon icon={faArrowLeft} />
          Voltar
        </Link>
      </div>

      {alert.show && (
        <div className={`alert-message alert-${alert.type}`}>
          <div className="alert-header">
            <span>{alert.type === 'success' ? 'Sucesso!' : 'Erro!'}</span>
            <button className="alert-close" onClick={() => setAlert({ show: false, type: '', message: '' })}>
              ×
            </button>
          </div>
          <div className="alert-body">
            {alert.message}
          </div>
        </div>
      )}

      <form onSubmit={(e) => e.preventDefault()}>
        <div className="form-section-card">
          <h2 className="form-section-title">
            <FontAwesomeIcon icon={faUser} className="section-icon" />
            Informações Pessoais
          </h2>

          <div className="form-grid">
            <div className="form-field">
              <label className="form-label">Nome do Usuário *</label>
              <input
                className={`form-input ${validate.name === false ? 'is-danger' : ''}`}
                name="name"
                type="text"
                value={values.name}
                onChange={(e) => handleChangeValues(e.target)}
                placeholder="Nome completo"
              />
              {validate.name === false && (
                <p className="form-help is-danger">Caracteres inválidos!</p>
              )}
            </div>

            <div className="form-field">
              <label className="form-label">E-mail *</label>
              <input
                className={`form-input ${validate.email === false ? 'is-danger' : ''}`}
                name="email"
                type="email"
                value={values.email}
                onChange={(e) => handleChangeValues(e.target)}
                placeholder="usuario@empresa.com.br"
              />
              {validate.email === false && (
                <p className="form-help is-danger">E-mail inválido!</p>
              )}
            </div>

            <div className="form-field">
              <label className="form-label">Senha *</label>
              <input
                className={`form-input ${validate.password === false ? 'is-danger' : ''}`}
                name="password"
                type="password"
                onBlur={(e) => handleChangeValues(e.target)}
                placeholder="Mínimo 6 caracteres"
              />
              {validate.password === false && (
                <p className="form-help is-danger">Verifique o tamanho e se existem caracteres inválidos!</p>
              )}
              <p className="form-help">Tamanho mínimo 6 caracteres</p>
            </div>

            <div className="form-field">
              <label className="form-label">Ramal</label>
              <FilterableSelect
                className="form-select"
                name="endpoint"
                options={endpointOptions}
                value={values.endpoint}
                placeholderOption={{ value: '', label: 'Selecione um ramal' }}
                filterPlaceholder="Filtrar ramais..."
                onChange={(e) => handleChangeValues(e.target)}
              />
            </div>

            {isRootVgs ? (
              <div className="form-field form-field-full">
                <label className="form-label">Clientes *</label>
                <FilterableSelect
                  className={`form-select form-select-multiple ${validate.tenants === false ? 'is-danger' : ''}`}
                  name="tenants"
                  multiple
                  options={tenantOptions}
                  value={values.tenants || []}
                  filterPlaceholder="Filtrar clientes..."
                  onChange={(e) => handleChangeValues(e.target)}
                />
                {validate.tenants === false && (
                  <p className="form-help is-danger">Selecione pelo menos um cliente válido!</p>
                )}
                <p className="form-help">Mantenha Ctrl pressionado para selecionar múltiplos clientes. Um usuário será criado para cada cliente selecionado.</p>
              </div>
            ) : (
              <div className="form-field">
                <label className="form-label">Cliente *</label>
                <select
                  className={`form-select ${validate.tenant === false ? 'is-danger' : ''}`}
                  name="tenant"
                  value={values.tenant}
                  onChange={(e) => handleChangeValues(e.target)}
                >
                  <option value="">Selecione</option>
                  {tenants.map((tenant) => (
                    <option key={`tenant-${tenant.short_name}`} value={tenant.short_name}>
                      {tenant.name}
                    </option>
                  ))}
                </select>
                {validate.tenant === false && (
                  <p className="form-help is-danger">Cliente selecionado não existe!</p>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="form-section-card">
          <h2 className="form-section-title">
            <FontAwesomeIcon icon={faShieldAlt} className="section-icon" />
            Permissões e Acesso
          </h2>

          <div className="form-grid">
            <div className="form-field">
              <label className="form-label">Tipo de Usuário *</label>
              <select
                className={`form-select ${validate.role === false ? 'is-danger' : ''}`}
                name="role"
                value={values.role}
                onChange={(e) => handleChangeValues(e.target)}
              >
                <option value="">Selecione</option>
                <option value="user">Básico</option>
                <option value="admin">Administrativo</option>
                <option value="tec_tenant">Técnico Cliente</option>
              </select>
              {validate.role === false && (
                <p className="form-help is-danger">Tipo de usuário inválido!</p>
              )}
            </div>
          </div>
        </div>

        <div className="form-section-card">
          <h2 className="form-section-title">
            <FontAwesomeIcon icon={faList} className="section-icon" />
            Visibilidade de Recursos
          </h2>

          <div className="form-grid">
            <div className="form-field">
              <label className="form-label">Exibir Ramais</label>
              <FilterableSelect
                className={`form-select form-select-multiple ${validate.endpointsviewer === false ? 'is-danger' : ''}`}
                name="endpointsviewer"
                multiple
                options={endpointsViewerOptions}
                pinnedValues={['all', 'none']}
                value={values.endpointsviewer || []}
                filterPlaceholder="Filtrar ramais..."
                onChange={(e) => handleChangeValues(e.target)}
              />
              {validate.endpointsviewer === false && (
                <p className="form-help is-danger">Caracteres inválidos!</p>
              )}
              <p className="form-help">Mantenha Ctrl pressionado para selecionar múltiplos itens</p>
            </div>

            <div className="form-field">
              <label className="form-label">Exibir Filas</label>
              <FilterableSelect
                className={`form-select form-select-multiple ${validate.queuesviewer === false ? 'is-danger' : ''}`}
                name="queuesviewer"
                multiple
                options={queuesViewerOptions}
                pinnedValues={['all', 'none']}
                value={values.queuesviewer || []}
                filterPlaceholder="Filtrar filas..."
                onChange={(e) => handleChangeValues(e.target)}
              />
              {validate.queuesviewer === false && (
                <p className="form-help is-danger">Caracteres inválidos!</p>
              )}
              <p className="form-help">Mantenha Ctrl pressionado para selecionar múltiplos itens</p>
            </div>
          </div>
        </div>

        {values.role === 'tec_tenant' && (
          <PermissionsTree
            value={values.enabled_modules}
            onChange={(grants) => setValues((prev) => ({ ...prev, enabled_modules: grants }))}
          />
        )}

        <div className="form-actions">
          <Link to="/config/usuarios/lista" className="btn-cancel">
            <FontAwesomeIcon icon={faArrowLeft} />
            Cancelar
          </Link>
          <button
            className="btn-submit"
            type="submit"
            onClick={sendDataUsersGenerate}
            disabled={disable || saving}
          >
            <FontAwesomeIcon icon={faPlus} />
            {saving ? 'Criando...' : 'Criar Usuário'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default UsersCreate;

